import { useClientStore, CLIENT_STORAGE_KEYS } from '@/client/client-store';
import { getAuth0Config, isAuth0Configured } from '@/auth/auth0';

/**
 * Route-scoped auth-provider resolution (PRTL-2857).
 *
 * Which provider a request uses is a property of the CURRENT ROUTE, not a
 * global toggle: the MFE app id in the pathname is checked against
 * `VITE_AUTH0_MFE_IDS`. This guarantees that visiting one Auth0 MFE does not
 * flip the shell's own calls (or other MSAL MFEs) onto Auth0 tokens. Only the
 * dedicated callback routes consult the stored `authProvider` flag.
 */

export type AuthProvider = 'msal' | 'auth0';

/** localStorage flag persisted by the Auth0 callback (parity with Angular POC). */
export const AUTH_PROVIDER_KEY = 'authProvider';

/**
 * The provider that owns the SHELL SESSION (PRTL-2857 independent mode).
 *
 * Driven by `VITE_AUTH_PROVIDER`: `auth0` makes the shell run entirely on Auth0
 * (Auth0-only login, session check, and shell tokens — no MSAL dependency);
 * anything else (default) keeps MSAL as the shell session with Auth0 layered
 * per-MFE. Returns `auth0` only when explicitly selected AND Auth0 is
 * configured, so a misconfiguration can never strand the shell without a
 * usable provider. An unreplaced `#{...}#` deploy token is treated as `msal`.
 */
export function getShellAuthProvider(): AuthProvider {
  let raw = (import.meta.env.VITE_AUTH_PROVIDER ?? '').trim().toLowerCase();
  // Deploy token replacement can leave surrounding quotes on the value (yml
  // `value: 'auth0'`); strip one layer so the comparison still matches.
  if (
    raw.length >= 2 &&
    ((raw.startsWith("'") && raw.endsWith("'")) || (raw.startsWith('"') && raw.endsWith('"')))
  ) {
    raw = raw.slice(1, -1).trim();
  }
  if (raw === 'auth0' && isAuth0Configured()) return 'auth0';
  return 'msal';
}

/** True when the shell session itself is owned by Auth0 (independent mode). */
export function isAuth0ShellSession(): boolean {
  return getShellAuthProvider() === 'auth0';
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isCallbackPath(pathname: string): boolean {
  return pathname === '/home' || pathname === '/auth0-callback';
}

/**
 * Extract the MFE app id from a shell pathname. Handles:
 *   /{clientId}/app-container/{appId}
 *   /{clientId}/app-container/workspace/apps/{appId}
 *   /{clientId}/app-container/admin/apps/{appId}
 *   /app/{appId}/...
 */
export function parseAppIdFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === 'app' && segments[1]) return segments[1];
  const containerIdx = segments.indexOf('app-container');
  if (containerIdx === -1) return null;
  const next = segments[containerIdx + 1];
  if (!next) return null;
  if ((next === 'workspace' || next === 'admin') && segments[containerIdx + 2] === 'apps') {
    return segments[containerIdx + 3] ?? null;
  }
  return next;
}

/** Extract the clientId from a `/{clientId}/app-container/...` pathname. */
export function parseClientIdFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[1] === 'app-container' && segments[0]) return segments[0];
  return null;
}

/** True when the given MFE app id is configured to use Auth0. */
export function isAuth0App(appId: string | null | undefined): boolean {
  if (!appId || !isAuth0Configured()) return false;
  return getAuth0Config().mfeIds.has(appId.toUpperCase());
}

/**
 * The provider a specific MFE requires. Used by the per-MFE token path
 * (`EY_RAPToR_getAccessTokenforMFE`).
 */
export function getAuthProviderForMfe(appId: string | null | undefined): AuthProvider {
  return isAuth0App(appId) ? 'auth0' : 'msal';
}

/**
 * Resolve the active provider for a route. Defaults to `'msal'`; only Auth0 MFE
 * routes (and the callback routes, via the stored flag) resolve to `'auth0'`.
 */
export function getAuthProvider(pathname: string = window.location.pathname): AuthProvider {
  if (!isAuth0Configured()) return 'msal';
  // Independent Auth0 shell (VITE_AUTH_PROVIDER=auth0): there is no MSAL
  // session, so EVERY route — and thus every MFE token — resolves to Auth0,
  // regardless of the per-MFE `VITE_AUTH0_MFE_IDS` allow-list.
  if (getShellAuthProvider() === 'auth0') return 'auth0';
  const { mfeIds } = getAuth0Config();

  const appId = parseAppIdFromPath(pathname);
  if (appId && mfeIds.has(appId.toUpperCase())) return 'auth0';

  // Nested/unknown shapes: any path segment matching an Auth0-enabled id wins.
  const segments = pathname.split('/').filter(Boolean);
  if (segments.some((s) => mfeIds.has(s.toUpperCase()))) return 'auth0';

  if (isCallbackPath(pathname)) {
    try {
      if (localStorage.getItem(AUTH_PROVIDER_KEY) === 'auth0') return 'auth0';
    } catch {
      /* storage unavailable */
    }
  }
  return 'msal';
}

/**
 * Strict clientId resolver for Auth0 token/redirect calls (PRTL-2857 1d).
 * Order: route/pathname → client store → sessionStorage → localStorage.
 * Returns `null` when nothing resolves so callers can fail fast instead of
 * sending an Auth0 request without `paramclientid`.
 */
export function resolveClientIdStrict(pathname: string = window.location.pathname): string | null {
  const fromPath = parseClientIdFromPath(pathname);
  if (fromPath && GUID_RE.test(fromPath)) return fromPath;

  const fromStore = useClientStore.getState().current?.id;
  if (fromStore) return fromStore;

  try {
    const s = sessionStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
    if (s) return s;
  } catch {
    /* storage unavailable */
  }
  try {
    const l = localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
    if (l) return l;
  } catch {
    /* storage unavailable */
  }
  return null;
}

/** Persist the active provider flag (callback + Auth0 login set `'auth0'`). */
export function setStoredAuthProvider(provider: AuthProvider): void {
  try {
    localStorage.setItem(AUTH_PROVIDER_KEY, provider);
  } catch {
    /* storage unavailable */
  }
}

/** Remove the stored provider flag (logout cleanup). */
export function clearStoredAuthProvider(): void {
  try {
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  } catch {
    /* storage unavailable */
  }
}
